import {
  canonicalModuleId,
  checkFit,
  pointDefinition,
  type CompiledCatalog,
} from "./catalog.ts";
import type {
  AssemblyNode,
  AssemblyPlan,
  AttachmentPointInstanceId,
  BuildRecipe,
  CatalogModule,
  PositionId,
  RecipeBase,
  RecipePlacement,
  RemovedSubtree,
  UnresolvedPosition,
  UnresolvedReason,
} from "./types.ts";

export function attachmentPointInstanceId(
  parentPositionId: PositionId,
  pointId: string,
): AttachmentPointInstanceId {
  return `${encodeURIComponent(parentPositionId)}::${encodeURIComponent(pointId)}`;
}

export function normalizeRecipe(
  incoming: BuildRecipe,
  catalog: CompiledCatalog,
  fallback: BuildRecipe,
): BuildRecipe {
  const fallbackBaseId = canonicalModuleId(catalog, fallback.base.moduleId);
  const fallbackBaseModule =
    fallbackBaseId == null ? undefined : catalog.moduleById.get(fallbackBaseId);
  if (fallbackBaseModule?.role !== "base") {
    throw new Error("The demonstration recipe must reference an available base module");
  }
  const canonicalFallbackBase: RecipeBase = {
    ...fallback.base,
    moduleId: fallbackBaseId!,
  };

  const unresolved: UnresolvedPosition[] = incoming.unresolved.map((position) =>
    structuredClone(position),
  );
  const desiredBaseId = canonicalModuleId(catalog, incoming.base.moduleId);
  const desiredBase = desiredBaseId == null ? undefined : catalog.moduleById.get(desiredBaseId);
  let base: RecipeBase;
  if (desiredBase?.role === "base") {
    base = { ...incoming.base, moduleId: desiredBaseId! };
  } else {
    base = canonicalFallbackBase;
    unresolved.push({
      id: unresolvedId("base", incoming.base.positionId),
      reason: desiredBase == null ? "base-missing" : "wrong-module-role",
      requestedModuleId: incoming.base.moduleId,
      parentPositionId: null,
      attachmentPointId: null,
      source: { ...incoming.base },
    });
  }

  const accepted: RecipePlacement[] = [];
  const positionModules = new Map<PositionId, CatalogModule>();
  positionModules.set(base.positionId, catalog.moduleById.get(base.moduleId)!);
  const usedPositionIds = new Set<PositionId>([base.positionId]);
  const occupiedPoints = new Set<string>();
  const pending = incoming.placements.map((source) => {
    const resolvedId = canonicalModuleId(catalog, source.moduleId);
    return {
      placement: resolvedId == null ? { ...source } : { ...source, moduleId: resolvedId },
      source: { ...source },
    };
  });

  // Resolve parent-first even if a recipe was serialized in another order.
  while (pending.length > 0) {
    let madeProgress = false;
    for (let index = pending.length - 1; index >= 0; index -= 1) {
      const pendingPlacement = pending[index]!;
      const placement = pendingPlacement.placement;
      const parentModule = positionModules.get(placement.parentPositionId);
      if (parentModule == null) continue;
      pending.splice(index, 1);
      madeProgress = true;

      const issue = placementIssue(
        placement,
        parentModule,
        occupiedPoints,
        usedPositionIds,
        catalog,
      );
      if (issue != null) {
        unresolved.push(
          toUnresolved(placement, issue.reason, issue.denyRuleId, pendingPlacement.source),
        );
        continue;
      }
      const childModule = catalog.moduleById.get(placement.moduleId)!;
      accepted.push(placement);
      positionModules.set(placement.positionId, childModule);
      usedPositionIds.add(placement.positionId);
      occupiedPoints.add(pointKey(placement.parentPositionId, placement.attachmentPointId));
    }
    if (!madeProgress) break;
  }

  for (const pendingPlacement of pending) {
    unresolved.push(
      toUnresolved(
        pendingPlacement.placement,
        "parent-unresolved",
        undefined,
        pendingPlacement.source,
      ),
    );
  }

  return {
    schema: 1,
    catalogFingerprint: catalog.source.fingerprint,
    base,
    placements: parentFirstPlacements(base, accepted),
    unresolved: uniqueUnresolved(unresolved),
    livery: { ...incoming.livery },
  };
}

function placementIssue(
  placement: RecipePlacement,
  parentModule: CatalogModule,
  occupiedPoints: ReadonlySet<string>,
  usedPositionIds: ReadonlySet<string>,
  catalog: CompiledCatalog,
): { reason: UnresolvedReason; denyRuleId?: string } | null {
  if (usedPositionIds.has(placement.positionId)) return { reason: "position-id-conflict" };
  const childModule = catalog.moduleById.get(placement.moduleId);
  if (childModule == null) return { reason: "module-missing" };
  if (childModule.role !== "module") return { reason: "wrong-module-role" };
  const point = pointDefinition(parentModule, placement.attachmentPointId);
  if (point == null) return { reason: "attachment-point-missing" };
  if (occupiedPoints.has(pointKey(placement.parentPositionId, placement.attachmentPointId))) {
    return { reason: "point-already-occupied" };
  }
  const fit = checkFit(point, childModule);
  if (!fit.ok) {
    if (fit.code === "EXPLICITLY_DENIED") {
      return { reason: "explicitly-denied", denyRuleId: fit.rule.ruleId };
    }
    return {
      reason: fit.code === "WRONG_MODULE_ROLE" ? "wrong-module-role" : "connection-type-mismatch",
    };
  }
  return null;
}

function toUnresolved(
  placement: RecipePlacement,
  reason: UnresolvedReason,
  denyRuleId?: string,
  source: RecipePlacement = placement,
): UnresolvedPosition {
  const base = {
    id: unresolvedId(placement.parentPositionId, placement.positionId),
    reason,
    requestedModuleId: source.moduleId,
    parentPositionId: placement.parentPositionId,
    attachmentPointId: placement.attachmentPointId,
    source: { ...source },
  };
  return denyRuleId == null ? base : { ...base, denyRuleId };
}

function uniqueUnresolved(unresolved: readonly UnresolvedPosition[]): readonly UnresolvedPosition[] {
  const result: UnresolvedPosition[] = [];
  const usedIds = new Set<string>();
  for (const position of unresolved) {
    let id = position.id;
    let suffix = 2;
    while (usedIds.has(id)) id = `${position.id}#${suffix++}`;
    usedIds.add(id);
    result.push(id === position.id ? structuredClone(position) : { ...structuredClone(position), id });
  }
  return result;
}

function unresolvedId(parent: string, position: string): string {
  return `unresolved:${encodeURIComponent(parent)}:${encodeURIComponent(position)}`;
}

function pointKey(parentPositionId: string, pointId: string): string {
  return `${parentPositionId}\u0000${pointId}`;
}

export function compileAssemblyPlan(
  recipe: BuildRecipe,
  catalog: CompiledCatalog,
): AssemblyPlan {
  const nodes: AssemblyNode[] = [];
  const baseModule = catalog.moduleById.get(recipe.base.moduleId);
  if (baseModule == null || baseModule.role !== "base") {
    throw new Error("Normalized recipe lost its base module");
  }
  nodes.push({
    positionId: recipe.base.positionId,
    moduleId: baseModule.id,
    label: baseModule.label,
    visualRef: baseModule.visualRef,
    parentPositionId: null,
    attachmentPointId: null,
    attachmentAnchor: null,
  });

  for (const placement of recipe.placements) {
    const module = catalog.moduleById.get(placement.moduleId);
    if (module == null) continue;
    const parentNode = nodes.find((node) => node.positionId === placement.parentPositionId);
    const parentModule =
      parentNode == null ? undefined : catalog.moduleById.get(parentNode.moduleId);
    const attachmentAnchor =
      parentModule == null
        ? undefined
        : pointDefinition(parentModule, placement.attachmentPointId)?.visualAnchor;
    if (attachmentAnchor == null) {
      throw new Error(
        `Normalized placement ${placement.positionId} lost visual anchor ${placement.attachmentPointId}`,
      );
    }
    nodes.push({
      positionId: placement.positionId,
      moduleId: placement.moduleId,
      label: module.label,
      visualRef: module.visualRef,
      parentPositionId: placement.parentPositionId,
      attachmentPointId: placement.attachmentPointId,
      attachmentAnchor,
    });
  }

  const occupied = new Map<string, PositionId>();
  for (const placement of recipe.placements) {
    occupied.set(pointKey(placement.parentPositionId, placement.attachmentPointId), placement.positionId);
  }
  const points = nodes.flatMap((node) => {
    const module = catalog.moduleById.get(node.moduleId)!;
    return module.attachmentPoints.map((point) => ({
      instanceId: attachmentPointInstanceId(node.positionId, point.id),
      parentPositionId: node.positionId,
      pointId: point.id,
      label: point.label,
      visualAnchor: point.visualAnchor,
      accepts: [...point.accepts],
      occupiedPositionId: occupied.get(pointKey(node.positionId, point.id)) ?? null,
    }));
  });

  return {
    nodes,
    points,
    livery: { ...recipe.livery },
    unresolved: recipe.unresolved.map((position) => structuredClone(position)),
  };
}

export interface RecipeTryOn {
  readonly recipe: BuildRecipe;
  readonly consequences: readonly RemovedSubtree[];
}

export function applyBase(
  recipe: BuildRecipe,
  moduleId: string,
  catalog: CompiledCatalog,
): RecipeTryOn {
  if (recipe.base.moduleId === moduleId) return { recipe, consequences: [] };

  const retainedIds = retainCompatibleDescendants(
    recipe.base.positionId,
    moduleId,
    recipe.placements,
    catalog,
  );
  const consequences: RemovedSubtree[] = [];
  for (const child of recipe.placements.filter(
    (placement) => placement.parentPositionId === recipe.base.positionId,
  )) {
    if (retainedIds.has(child.positionId)) continue;
    consequences.push(removedSubtree(child, recipe.placements));
  }

  const placements = recipe.placements.filter((placement) =>
    retainedIds.has(placement.positionId),
  );
  const base = { ...recipe.base, moduleId };
  return {
    recipe: {
      ...recipe,
      base,
      placements: parentFirstPlacements(base, placements),
    },
    consequences,
  };
}

export function applyAtPoint(
  recipe: BuildRecipe,
  parentPositionId: PositionId,
  attachmentPointId: string,
  moduleId: string | null,
  catalog: CompiledCatalog,
): RecipeTryOn {
  const current = recipe.placements.find(
    (placement) =>
      placement.parentPositionId === parentPositionId &&
      placement.attachmentPointId === attachmentPointId,
  );
  if (current == null && moduleId == null) return { recipe, consequences: [] };
  if (current?.moduleId === moduleId) return { recipe, consequences: [] };

  const descendants = current == null ? [] : descendantsOf(current.positionId, recipe.placements);
  let retainedDescendantIds = new Set<PositionId>();
  const consequences: RemovedSubtree[] = [];
  const targetPositionId = current?.positionId ?? derivedPositionId(parentPositionId, attachmentPointId);

  if (current != null && moduleId != null) {
    retainedDescendantIds = retainCompatibleDescendants(
      current.positionId,
      moduleId,
      recipe.placements,
      catalog,
    );
  }

  if (current != null) {
    const directChildren = recipe.placements.filter(
      (placement) => placement.parentPositionId === current.positionId,
    );
    for (const child of directChildren) {
      if (retainedDescendantIds.has(child.positionId)) continue;
      consequences.push(removedSubtree(child, recipe.placements));
    }
  }

  const removedIds = new Set<PositionId>();
  if (current != null) {
    removedIds.add(current.positionId);
    for (const placement of descendants) {
      if (!retainedDescendantIds.has(placement.positionId)) removedIds.add(placement.positionId);
    }
  }

  const kept = recipe.placements.filter((placement) => !removedIds.has(placement.positionId));
  const replacement =
    moduleId == null
      ? []
      : [
          {
            positionId: targetPositionId,
            parentPositionId,
            attachmentPointId,
            moduleId,
          },
        ];
  return {
    recipe: {
      ...recipe,
      placements: parentFirstPlacements(recipe.base, [...kept, ...replacement]),
    },
    consequences,
  };
}

function removedSubtree(
  root: RecipePlacement,
  placements: readonly RecipePlacement[],
): RemovedSubtree {
  const positionIds = [
    root.positionId,
    ...descendantsOf(root.positionId, placements).map((placement) => placement.positionId),
  ];
  return {
    rootPositionId: root.positionId,
    rootModuleId: root.moduleId,
    positionIds,
    moduleCount: positionIds.length,
  };
}

function retainCompatibleDescendants(
  replacedPositionId: PositionId,
  replacementModuleId: string,
  placements: readonly RecipePlacement[],
  catalog: CompiledCatalog,
): Set<PositionId> {
  const retained = new Set<PositionId>();
  const replacementModule = catalog.moduleById.get(replacementModuleId);
  if (replacementModule == null) return retained;
  const queue: Array<{ positionId: PositionId; module: CatalogModule }> = [
    { positionId: replacedPositionId, module: replacementModule },
  ];
  while (queue.length > 0) {
    const parent = queue.shift()!;
    for (const child of placements.filter((item) => item.parentPositionId === parent.positionId)) {
      const point = pointDefinition(parent.module, child.attachmentPointId);
      const childModule = catalog.moduleById.get(child.moduleId);
      if (point == null || childModule == null || !checkFit(point, childModule).ok) continue;
      retained.add(child.positionId);
      queue.push({ positionId: child.positionId, module: childModule });
    }
  }
  return retained;
}

function descendantsOf(
  root: PositionId,
  placements: readonly RecipePlacement[],
): readonly RecipePlacement[] {
  const descendants: RecipePlacement[] = [];
  const queue = [root];
  while (queue.length > 0) {
    const parent = queue.shift()!;
    for (const placement of placements) {
      if (placement.parentPositionId !== parent) continue;
      descendants.push(placement);
      queue.push(placement.positionId);
    }
  }
  return descendants;
}

function parentFirstPlacements(
  base: RecipeBase,
  placements: readonly RecipePlacement[],
): readonly RecipePlacement[] {
  const result: RecipePlacement[] = [];
  const pending = placements.map((placement) => ({ ...placement }));
  const resolved = new Set<PositionId>([base.positionId]);
  while (pending.length > 0) {
    const before = pending.length;
    for (let index = 0; index < pending.length; ) {
      const placement = pending[index]!;
      if (!resolved.has(placement.parentPositionId)) {
        index += 1;
        continue;
      }
      result.push(placement);
      resolved.add(placement.positionId);
      pending.splice(index, 1);
    }
    if (pending.length === before) {
      result.push(...pending);
      break;
    }
  }
  return result;
}

function derivedPositionId(parentPositionId: string, attachmentPointId: string): PositionId {
  return `position:${encodeURIComponent(parentPositionId)}:${encodeURIComponent(attachmentPointId)}`;
}
